import React, { useState, useRef, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import { SecondLevelDrawer } from "@/features/navigation/components/SecondLevelDrawer";
import type { MenuIconComponent } from "@/features/navigation/utils/iconResolver";
import { isNavigationItemActive, shouldIncludeChildRoutes } from "@/features/navigation/utils/navigationUtils";
import { SidebarMotionLink } from "@/shared/components/SidebarMotionLink";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/shared/components/ui/sidebar";

const DRAWER_QUERY_PARAM = "drawer";

type NavigationItemWithDrawerProps = {
  to: string
  label: string
  icon: MenuIconComponent
  children: React.ReactNode
  forceOpen?: boolean
  isSidebarOpen?: boolean
  onMainNavigationClose?: () => void
  mayCloseOnClickOutside?: boolean
  closeOnLinkClick?: boolean
  drawerPersistenceKey?: string
  onBack?: () => void
  hideBackButton?: boolean
  /** Highlight decided by the whole menu (see getActiveMenuKey); falls back to the route match. */
  isActive?: boolean
  /** Optional node rendered next to the label (e.g. an unread-count badge). */
  badge?: React.ReactNode
  /** Monte le drawer sans rendre de bouton trigger dans la sidebar.
   * Utilisé pour les drawers qui se déclenchent uniquement via la
   * route (auto-open sur `/catalogue*`) — sans rien afficher au
   * niveau menu principal. */
  hideTrigger?: boolean
};

export const NavigationItemWithDrawer = ({
  to,
  label,
  icon: IconComponent,
  children,
  forceOpen = false,
  isSidebarOpen: isSidebarOpenProp,
  onMainNavigationClose,
  mayCloseOnClickOutside = false,
  closeOnLinkClick,
  drawerPersistenceKey,
  onBack,
  hideBackButton = false,
  hideTrigger = false,
  badge,
  isActive: isActiveProp,
}: NavigationItemWithDrawerProps) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile, open: sidebarOpen } = useSidebar();
  const [isDrawerOpen, setIsDrawerOpen] = useState(forceOpen);
  const triggerRef = useRef<HTMLLIElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const drawerParamValue = searchParams.get(DRAWER_QUERY_PARAM);

  const isSidebarOpen = isSidebarOpenProp ?? sidebarOpen;
  const isSidebarCollapsed = !sidebarOpen && !isMobile;

  const isActive = isActiveProp
    ?? isNavigationItemActive(to, location.pathname, { includeChildRoutes: shouldIncludeChildRoutes(to) });
  const targetLink = drawerPersistenceKey
    ? { pathname: to, search: `?${DRAWER_QUERY_PARAM}=${drawerPersistenceKey}` }
    : to;
  const isBlogRoute = to === "/blog";
  const isOnBlogPage = location.pathname.startsWith("/blog");
  const isDealsRoute = to === "/bon-plans";
  const isOnDealsPage = location.pathname.startsWith("/bon-plans")
    && !location.pathname.startsWith("/bon-plans/commandes/");
  const isBoutiqueRoute = to === "/boutique";
  // Vues transverses sous `/boutique/*` qui ne sont PAS de la
  // boutique au sens « catalogue produits » — `/boutique/commandes`
  // (mes commandes, agrège boutique + bons plans + locations) et
  // `/boutique/panier` (le panier unifié qui héberge les 3 cart
  // types). Ces pages ne doivent jamais auto-ouvrir le drawer
  // Boutique, peu importe `?cartType=` ou `?orderType=` choisi.
  const isOnTransverseBoutiquePage =
    location.pathname.startsWith("/boutique/commandes")
    || location.pathname.startsWith("/boutique/panier");
  const isOnBoutiquePage = location.pathname.startsWith("/boutique")
    && location.pathname !== "/boutique/camping/checkout"
    && !isOnTransverseBoutiquePage;
  const isLocationsRoute = to === "/catalogue";
  // On considère qu'on est « sur » la page catalogue dès qu'on
  // navigue à un sous-chemin (item détail, checkout) — le drawer
  // reste donc affiché et l'élu peut filtrer/relancer une recherche.
  const isOnLocationsPage = location.pathname.startsWith("/catalogue");

  useEffect(() => {
    const isOnTargetRoute = location.pathname.startsWith(to)
      && !(isDealsRoute && !isOnDealsPage);
    const shouldAutoOpen =
      (isBlogRoute && isOnBlogPage)
      || (isDealsRoute && isOnDealsPage)
      || (isBoutiqueRoute && isOnBoutiquePage)
      || (isLocationsRoute && isOnLocationsPage);

    if (forceOpen) {
      setIsDrawerOpen(true);
      return;
    }
    if (drawerPersistenceKey && !isOnTargetRoute) {
      setIsDrawerOpen(false);
      if (drawerParamValue === drawerPersistenceKey) {
        const params = new URLSearchParams(searchParams);
        params.delete(DRAWER_QUERY_PARAM);
        setSearchParams(params, { replace: true });
      }
      return;
    }
    // Vues transverses (mes commandes, panier unifié) : on ferme
    // activement le drawer Boutique si jamais il était resté ouvert
    // depuis une page précédente (sidebar accordion ou
    // ?drawer=boutique persistant). Pas de strip du param URL —
    // cf. commentaire dans OrdersPage.tsx (loop infini).
    if (isBoutiqueRoute && isOnTransverseBoutiquePage) {
      setIsDrawerOpen(false);
      return;
    }
    if (shouldAutoOpen && !isMobile) {
      setIsDrawerOpen(true);
      return;
    }
    if ((isBlogRoute || isDealsRoute || isBoutiqueRoute || isLocationsRoute) && isMobile) {
      setIsDrawerOpen(false);
      return;
    }
    // Le drawer Boutique ne doit pas se rouvrir via le param
    // persistant `?drawer=boutique` quand on est sur une vue
    // transverse (Mes commandes / Panier unifié) — ces pages
    // agrègent boutique + bons plans + locations.
    if (
      drawerPersistenceKey
      && drawerParamValue === drawerPersistenceKey
      && !(isBoutiqueRoute && isOnTransverseBoutiquePage)
    ) {
      setIsDrawerOpen(true);
    }
  }, [
    forceOpen,
    drawerPersistenceKey,
    drawerParamValue,
    isBlogRoute,
    isOnBlogPage,
    isDealsRoute,
    isOnDealsPage,
    isBoutiqueRoute,
    isOnBoutiquePage,
    isOnTransverseBoutiquePage,
    isLocationsRoute,
    isOnLocationsPage,
    isMobile,
    location.pathname,
    location.search,
    to,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!drawerPersistenceKey) return;

    const isOnTargetRoute = location.pathname.startsWith(to)
      && !(isDealsRoute && !isOnDealsPage);
    if (!isOnTargetRoute) return;

    if (isDrawerOpen && drawerParamValue !== drawerPersistenceKey) {
      const params = new URLSearchParams(searchParams);
      params.set(DRAWER_QUERY_PARAM, drawerPersistenceKey);
      setSearchParams(params, { replace: true });
    }
    else if (!isDrawerOpen && drawerParamValue === drawerPersistenceKey) {
      const params = new URLSearchParams(searchParams);
      params.delete(DRAWER_QUERY_PARAM);
      setSearchParams(params, { replace: true });
    }
  }, [
    drawerPersistenceKey,
    drawerParamValue,
    isDrawerOpen,
    location.pathname,
    location.search,
    to,
    setSearchParams,
  ]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDrawerOpen(false);
  };

  return (
    <>
      {!hideTrigger && (
        <SidebarMenuItem ref={triggerRef}>
          <SidebarMenuButton
            asChild
            isActive={isActive}
            onClick={handleClick}
            className="group/link hover:bg-sidebar-primary/10 hover:text-sidebar-primary dark:hover:bg-sidebar-primary/15"
          >
            <SidebarMotionLink
              to={targetLink}
              disableHover={isSidebarCollapsed}
            >
              <IconComponent size={20} className="text-sidebar-icon-foreground shrink-0 group-hover/link:text-sidebar-primary" />
              <span className="truncate">{label}</span>
              {badge}
            </SidebarMotionLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}

      <SecondLevelDrawer
        isOpen={isDrawerOpen}
        isSidebarOpen={isSidebarOpen}
        onClose={handleDrawerClose}
        onBack={onBack}
        onLinkClick={onMainNavigationClose}
        mayCloseOnClickOutside={mayCloseOnClickOutside}
        closeOnLinkClick={closeOnLinkClick}
        hideBackButton={hideBackButton}
      >
        {children}
      </SecondLevelDrawer>
    </>
  );
};

NavigationItemWithDrawer.displayName = "NavigationItemWithDrawer";
